import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class PhonepeUpiSearchService {
  baseUrl: string = environment.baseUrl;
  constructor(private http: HttpClient) {}

  getPhonepeUpiSearchHistory(
    page: any,
    limit: any,
    searchKey?: any,
    searchValue?: any,
    startDate?: any,
    endDate?: any,
    shouldExport?: any,
    exportFormat?: any,
  ): Observable<any> {
    let params = new HttpParams().set('page', page).set('size', limit);

    if (searchKey) {
      params = params.set('search_key', searchKey);
    }

    if (searchValue) {
      params = params.set('search_value', searchValue);
    }

    if (startDate) {
      params = params.set('start_date', startDate);
    }

    if (endDate) {
      params = params.set('end_date', endDate);
    }

    if (shouldExport) {
      params = params.set('is_export', shouldExport);
    }

    if (exportFormat) {
      params = params.set('format', exportFormat);
    }

    return this.http.get(`${this.baseUrl}/phonepe-upi/history`, { params });
  }

  searchPhonepeUpi(payload: any) {
    return this.http.post(`${this.baseUrl}/phonepe-upi/search`, payload);
  }

  deletePhonepeUpiSearchHistory(payload: any) {
    return this.http.delete(`${this.baseUrl}/phonepe-upi/delete`, {
      body: payload,
    });
  }

  getSearchResults(jobIds: string[]): Observable<any> {
    const ids = [...new Set((jobIds || []).filter(Boolean))];
    if (!ids.length) {
      return of({ data: { jobs: [] } });
    }

    if (ids.length === 1) {
      return this.fetchOneJob(ids[0]);
    }

    return forkJoin(ids.map((id) => this.fetchOneJob(id))).pipe(
      map((responses) => ({
        data: {
          jobs: responses.flatMap((response) => response?.data?.jobs || []),
        },
      })),
    );
  }

  private fetchOneJob(jobId: string): Observable<any> {
    return this.http
      .get(`${this.baseUrl}/phonepe-upi/job?job_id=${jobId}`)
      .pipe(
        map((response: any) => this.normalizeJobResponse(jobId, response)),
        catchError((error) => {
          if (error?.error?.data) {
            return of(
              this.normalizeJobResponse(jobId, { data: error.error.data }),
            );
          }
          return of({
            data: {
              jobs: [
                {
                  job_id: jobId,
                  done: true,
                  status: error?.status === 404 ? 'deleted' : 'failed',
                  deleted: error?.status === 404,
                  background_refresh_pending: false,
                },
              ],
            },
          });
        }),
      );
  }

  private normalizeJobResponse(jobId: string, response: any) {
    const data = response?.data || {};
    return {
      data: {
        jobs: [
          {
            job_id: data.job_ids?.[0] || data.job_id || jobId,
            done: Boolean(data.done),
            status: data.status || (data.done ? 'completed' : 'pending'),
            background_refresh_pending: Boolean(data.background_refresh_pending),
            phonepe_upi_search_id: data.phonepe_upi_search_id,
            credit_refunded: data.credit_refunded,
            notes: data.notes,
            message: data.message,
            searched_upi: data.searched_upi,
          },
        ],
      },
    };
  }

  getCompletedSearchResult(searchId: string): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/phonepe-upi/results/?phonepe_upi_search_id=${searchId}`,
    );
  }

  exportSearchResult(searchId: string, format: string): Observable<any> {
    const params = new HttpParams()
      .set('phonepe_upi_search_id', searchId)
      .set('is_export', true)
      .set('format', format);

    return this.http.get(`${this.baseUrl}/phonepe-upi/results/`, { params });
  }

  updatePhonepeUpiNotes(payload: any): Observable<any> {
    return this.http.put(`${this.baseUrl}/phonepe-upi/update`, payload);
  }
}
